import { useEffect, useState } from 'react';
import { Alert, Button, Checkbox, Input } from 'antd';
import { CheckOutlined, SendOutlined } from '@ant-design/icons';
import { initiateAadhaar, verifyAadhaar } from '@/api/dataroom';
import { normalizeError } from '@/api/errors';
import type { AadhaarInitiated, DeclarationSpec, Task, TaskStatus } from '@/api/types';
import { useI18n } from '@/i18n/useI18n';
import { useTaskText } from './taskText';
import { checkAadhaar, normalizeAadhaar } from '@/validation/formats';

/** Server refusal codes (`AADHAAR_<reason>`) the room has copy for; anything else is the generic line. */
const KNOWN = new Set([
  'AADHAAR_NUMBER_INVALID',
  'AADHAAR_SEND_LIMIT',
  'AADHAAR_RESEND_TOO_SOON',
  'AADHAAR_NO_ACTIVE_CODE',
  'AADHAAR_CODE_WRONG',
  'AADHAAR_CODE_EXPIRED',
  'AADHAAR_CODE_LOCKED',
  'AADHAAR_UNAVAILABLE',
]);

/**
 * AADHAAR_OTP task (OL-22 slice 3): consent, then the Aadhaar number, then the code UIDAI sends to
 * the Aadhaar-linked mobile. The number field is masked as it is typed and is cleared the moment the
 * server accepts it; it is held in this component's state only, and never written to storage. A
 * resend asks for it again. When the hub has no live e-KYC entitlement the step does not render.
 */
export function AadhaarOtpTask({
  task,
  onStatus,
}: {
  task: Task;
  onStatus: (status: TaskStatus) => void;
}) {
  const { t } = useI18n();
  const text = useTaskText(task);
  const version = (task.spec as DeclarationSpec | undefined)?.version ?? '';
  const [accepted, setAccepted] = useState(false);
  const [number, setNumber] = useState('');
  const [sent, setSent] = useState<AadhaarInitiated | null>(null);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [wait, setWait] = useState(0);

  useEffect(() => {
    if (wait <= 0) return undefined;
    const id = setTimeout(() => setWait((w) => w - 1), 1000);
    return () => clearTimeout(id);
  }, [wait]);

  if (task.status === 'SUBMITTED' || task.status === 'ACCEPTED' || done) {
    return <Alert type="success" showIcon message={t('aadhaar.verified')} />;
  }
  if (task.available === false) {
    return <Alert type="info" showIcon message={t('aadhaar.unavailable')} data-testid="aadhaar-unavailable" />;
  }

  function explain(err: unknown) {
    const e = normalizeError(err);
    setError(e.code && KNOWN.has(e.code) ? t(`aadhaar.error.${e.code}`) : t('error.inline'));
    return e;
  }

  const normalized = normalizeAadhaar(number);
  const invalid = normalized.length >= 12 && checkAadhaar(normalized) !== null;

  async function send() {
    setBusy(true);
    setError(null);
    try {
      const res = await initiateAadhaar(task.taskId, version, normalized);
      setSent(res);
      setNumber('');
      setCode('');
      setWait(res.resendAfterSeconds);
    } catch (err) {
      explain(err);
    } finally {
      setBusy(false);
    }
  }

  async function verify() {
    setBusy(true);
    setError(null);
    try {
      const res = await verifyAadhaar(task.taskId, code);
      onStatus(res.status);
      setDone(true);
    } catch (err) {
      const e = explain(err);
      if (e.code === 'AADHAAR_CODE_LOCKED' || e.code === 'AADHAAR_CODE_EXPIRED') setCode('');
    } finally {
      setBusy(false);
    }
  }

  if (!sent) {
    return (
      <div className="dr-stack">
        <div className="dr-declaration dr-legal" data-testid="aadhaar-consent-text">
          {task.declarationText}
        </div>
        <Checkbox
          className="dr-legal"
          checked={accepted}
          onChange={(e) => setAccepted(e.target.checked)}
          data-testid="aadhaar-consent"
        >
          {t('aadhaar.consent')}
        </Checkbox>
        <label className="dr-field">
          <span>{text('numberLabel', 'aadhaar.numberLabel')}</span>
          <Input.Password
            size="large"
            value={number}
            visibilityToggle={false}
            inputMode="numeric"
            autoComplete="off"
            maxLength={14}
            disabled={!accepted}
            onChange={(e) => setNumber(e.target.value.replace(/[^0-9 ]/g, ''))}
            data-testid="aadhaar-number"
          />
        </label>
        {invalid && <Alert type="warning" showIcon message={t('aadhaar.invalid')} />}
        {error && <Alert type="error" showIcon message={error} />}
        <Button
          type="primary"
          size="large"
          block
          disabled={!accepted || checkAadhaar(normalized) !== null}
          loading={busy}
          icon={<SendOutlined />}
          onClick={send}
          data-testid="aadhaar-send"
        >
          {text('send', 'aadhaar.send')}
        </Button>
      </div>
    );
  }

  return (
    <div className="dr-stack">
      <div className="dr-meta">{t('aadhaar.sentTo', { masked: sent.maskedNumber })}</div>
      {sent.demoCode && (
        <Alert
          type="info"
          showIcon
          message={t('aadhaar.demoCode', { code: sent.demoCode })}
          data-testid="aadhaar-demo-code"
        />
      )}
      <label className="dr-field">
        <span>{text('codeLabel', 'aadhaar.codeLabel')}</span>
        <Input
          size="large"
          className="dr-otp__code"
          value={code}
          maxLength={6}
          inputMode="numeric"
          autoComplete="one-time-code"
          onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
          data-testid="aadhaar-code"
        />
      </label>
      {error && <Alert type="error" showIcon message={error} />}
      <Button
        type="primary"
        size="large"
        block
        disabled={code.length !== 6}
        loading={busy}
        icon={<CheckOutlined />}
        onClick={verify}
        data-testid="aadhaar-verify"
      >
        {text('submit', 'aadhaar.verify')}
      </Button>
      <div className="dr-otp__actions">
        <Button
          type="link"
          disabled={busy || wait > 0 || sent.sendsLeft <= 0}
          onClick={() => {
            setSent(null);
            setError(null);
          }}
        >
          {wait > 0 ? t('aadhaar.resendIn', { seconds: wait }) : t('aadhaar.resend')}
        </Button>
      </div>
    </div>
  );
}
